/**
 * Labels. Hover label text follows RenderingViewModel.Atomistic.cs:631-646 (atoms) and RenderingViewModel.Ribbon.cs:
 * 1049-1063 (ribbon: same without the atom line); it is shown as an HTML overlay inside the mounted container.
 * Residue labels ("{ResName} {ResSerial}" at the residue centroid, RenderingViewModel.ResidueLabel.cs:112-127) are
 * canvas-atlas billboards. Nanome draws both with TextMeshPro LiberationSans SDF, white: HighlightLabel.prefab
 * fontSize 10 / scale 0.3, ResidueLabel.prefab fontSize 12 / scale 0.6.
 */
import { CanvasTexture, InstancedBufferAttribute, LinearFilter, Mesh, RawShaderMaterial, type InstancedBufferGeometry } from 'three';
import type { NanomeModel } from '@nanome-lite/data';
import { INTERACTION } from '@nanome-lite/look';
import { labelQuadGeometry, makeMaterial, type SharedUniforms } from './gl';
import { RENDER_ORDER } from './impostors';
import { LABEL_FRAG, LABEL_VERT } from './shaders';

const ELEMENT_SYMBOLS = ['X', 'H', 'He', 'Li', 'Be', 'B', 'C', 'N', 'O', 'F', 'Ne', 'Na', 'Mg', 'Al', 'Si', 'P', 'S', 'Cl', 'Ar', 'K', 'Ca', 'Sc', 'Ti', 'V', 'Cr', 'Mn', 'Fe', 'Co', 'Ni', 'Cu', 'Zn', 'Ga', 'Ge', 'As', 'Se', 'Br', 'Kr', 'Rb', 'Sr', 'Y', 'Zr', 'Nb', 'Mo', 'Tc', 'Ru', 'Rh', 'Pd', 'Ag', 'Cd', 'In', 'Sn', 'Sb', 'Te', 'I', 'Xe', 'Cs', 'Ba', 'La', 'Ce', 'Pr', 'Nd', 'Pm', 'Sm', 'Eu', 'Gd', 'Tb', 'Dy', 'Ho', 'Er', 'Tm', 'Yb', 'Lu', 'Hf', 'Ta', 'W', 'Re', 'Os', 'Ir', 'Pt', 'Au', 'Hg', 'Tl', 'Pb', 'Bi', 'Po', 'At', 'Rn', 'Fr', 'Ra', 'Ac', 'Th', 'Pa', 'U', 'Np', 'Pu', 'Am', 'Cm', 'Bk', 'Cf', 'Es', 'Fm', 'Md', 'No', 'Lr', 'Rf', 'Db', 'Sg', 'Bh', 'Hs', 'Mt'];

export const elementSymbol = (z: number): string => ELEMENT_SYMBOLS[z] ?? 'X';

/** StringExt.Ellipsis (Common/Extensions/StringExt.cs:9-12): truncate to maxChars and append "...". */
export function ellipsis(s: string, maxChars: number, ellipsisStr = '...'): string {
  if (!s) return s;
  return s.length <= maxChars ? s : s.slice(0, maxChars) + ellipsisStr;
}

const blank = (s: string | undefined) => !s || s.trim() === '';

/**
 * AtomToLabel / AtomToRibbonLabel. `<b>{entry≤7}</b>\n<b>{chain}</b>|{resn}'{resi}\n{atomName}`; the ribbon variant
 * drops the atom line. Missing chain or residue name falls back to the atom name, then "{Element}:{Serial}".
 */
export function formatHoverLabel(model: NanomeModel, atomIndex: number, entryName: string, ribbon: boolean, displayEntryName = true): string {
  const ri = model.residueIndex[atomIndex]!;
  const chain = model.chains.name[model.chainIndex[atomIndex]!];
  const resName = model.residues.name[ri];
  const atomName = model.atomName[atomIndex];
  const entry = displayEntryName ? `<b>${ellipsis(entryName, INTERACTION.entryNameEllipsis)}</b>\n` : '';
  if (blank(chain) || blank(resName)) {
    if (blank(atomName)) return `${elementSymbol(model.element[atomIndex]!)}:${model.atomSerial[atomIndex]}`;
    return atomName!;
  }
  const head = `${entry}<b>${chain}</b>|${resName}'${model.residues.serial[ri]}`;
  return ribbon ? head : `${head}\n${atomName ?? ''}`;
}

/** "{ResName} {ResSerial}" (RenderingViewModel.ResidueLabel.cs:123). */
export function formatResidueLabel(resName: string, resSerial: number): string {
  return `${resName} ${resSerial}`;
}

/** Nanome rich-text (<b> only) -> HTML with the line breaks kept. */
export function labelToHtml(text: string): string {
  const esc = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return esc.replace(/&lt;b&gt;/g, '<b>').replace(/&lt;\/b&gt;/g, '</b>').replace(/\n/g, '<br>');
}

/** HTML hover label owned by the engine inside the mounted container. */
export class HoverLabelOverlay {
  readonly el: HTMLDivElement;
  constructor(container: HTMLElement) {
    const el = document.createElement('div');
    el.className = 'nanome-lite-hover-label';
    Object.assign(el.style, {
      position: 'absolute', left: '0', top: '0', pointerEvents: 'none', display: 'none', color: '#fff',
      font: `${INTERACTION.hoverLabel.fontSize * 1.3}px/1.25 "Liberation Sans", Arial, Helvetica, sans-serif`,
      whiteSpace: 'pre', textShadow: '0 0 3px rgba(0,0,0,0.9), 0 0 1px rgba(0,0,0,1)', transform: 'translate(12px, -100%)', zIndex: '2',
    } as Partial<CSSStyleDeclaration>);
    container.appendChild(el);
    this.el = el;
  }
  show(text: string, x: number, y: number): void {
    this.el.innerHTML = labelToHtml(text);
    this.el.style.transform = `translate(${Math.round(x) + 12}px, ${Math.round(y) - 8}px) translateY(-100%)`;
    this.el.style.display = 'block';
  }
  hide(): void { this.el.style.display = 'none'; }
  dispose(): void { this.el.remove(); }
}

export interface ResidueLabelItem { text: string; center: [number, number, number]; color: [number, number, number] }

/**
 * TextMeshPro world text: 1 font-size unit is 0.1 world units, so fontSize 12 x scale 0.6 is a 0.72-unit line in the
 * entry's local space (Å). Line height in Å for residue labels; tunable via EngineOptions.
 */
export const RESIDUE_LABEL_LINE_HEIGHT_ANGSTROM = INTERACTION.residueLabel.fontSize * 0.1 * INTERACTION.residueLabel.scale;

/** Instanced billboards over one canvas atlas per batch. */
export class ResidueLabelBatch {
  readonly mesh: Mesh;
  private readonly geometry: InstancedBufferGeometry;
  private readonly texture: CanvasTexture;
  private readonly material: RawShaderMaterial;

  constructor(items: ResidueLabelItem[], shared: SharedUniforms, lineHeightAngstrom = RESIDUE_LABEL_LINE_HEIGHT_ANGSTROM) {
    const fontPx = 48;
    const pad = 4;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    ctx.font = `${fontPx}px "Liberation Sans", Arial, Helvetica, sans-serif`;
    const widths = items.map((it) => Math.ceil(ctx.measureText(it.text).width) + pad * 2);
    const rowH = fontPx + pad * 2;
    const atlasW = Math.min(4096, Math.max(64, nextPow2(Math.max(...widths, 1))));
    let x = 0, y = 0;
    const place: { x: number; y: number; w: number }[] = [];
    for (const w of widths) {
      if (x + w > atlasW) { x = 0; y += rowH; }
      place.push({ x, y, w }); x += w;
    }
    const atlasH = Math.max(64, nextPow2(y + rowH));
    canvas.width = atlasW; canvas.height = atlasH;
    ctx.clearRect(0, 0, atlasW, atlasH);
    ctx.font = `${fontPx}px "Liberation Sans", Arial, Helvetica, sans-serif`;
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#fff';
    items.forEach((it, i) => ctx.fillText(it.text, place[i]!.x + pad, place[i]!.y + pad));
    this.texture = new CanvasTexture(canvas);
    this.texture.minFilter = LinearFilter; this.texture.magFilter = LinearFilter; this.texture.generateMipmaps = false;
    this.texture.flipY = false;

    const n = items.length;
    const g = labelQuadGeometry();
    g.instanceCount = n;
    const center = new Float32Array(n * 3), uv = new Float32Array(n * 4), size = new Float32Array(n * 2), color = new Float32Array(n * 3);
    items.forEach((it, i) => {
      center.set(it.center, i * 3);
      const p = place[i]!;
      // canvas row 0 is the top; the quad's y = -1 corner is the bottom, so v runs from the row's bottom edge up
      uv.set([p.x / atlasW, (p.y + rowH) / atlasH, (p.x + p.w) / atlasW, p.y / atlasH], i * 4);
      size.set([(lineHeightAngstrom * p.w) / rowH, lineHeightAngstrom], i * 2);
      color.set(it.color, i * 3);
    });
    g.setAttribute('aCenter', new InstancedBufferAttribute(center, 3));
    g.setAttribute('aUvRect', new InstancedBufferAttribute(uv, 4));
    g.setAttribute('aSize', new InstancedBufferAttribute(size, 2));
    g.setAttribute('aColor', new InstancedBufferAttribute(color, 3));
    this.geometry = g;
    this.material = makeMaterial(LABEL_VERT, LABEL_FRAG, shared, { uniforms: { uAtlas: { value: this.texture } }, blend: true, depthWrite: false });
    this.mesh = new Mesh(g, this.material);
    this.mesh.frustumCulled = false; this.mesh.matrixAutoUpdate = false; this.mesh.renderOrder = RENDER_ORDER.label;
  }

  dispose(): void { this.geometry.dispose(); this.texture.dispose(); this.material.dispose(); }
}

function nextPow2(v: number): number { let p = 1; while (p < v) p *= 2; return p; }
